import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { getTokenFromCookies } from "@/utils/common";
import { formatPhoneNumber } from "@/utils/format";

type ColorTheme = "moiOperator" | "psb" | "atom";

interface MenuItem {
  title: string;
  icon: string | null;
  active: boolean;
  page: string;
}

export function useAsideMenu() {
  const router = useRouter();
  const route = useRoute();
  const store = useStore();
  const { t, locale } = useI18n();

  const isAsideHidden = computed(() => store.getters.isAsideHidden);
  const isLogoHidden = computed(() => store.getters.isLogoHidden);
  const isAuth = computed(() => store.state.auth.isAuth);
  const isShowList = ref(false);
  const isLoadingSelectPhone = ref(false);

  const configDataBrand = computed(
    () => store.getters["brand/configDataBrand"]
  );

  const currentUserPhone = computed<number>(
    () => store.getters["tariff/currentUserPhone"]
  );

  const menuList = ref<MenuItem[]>([
    { title: "", icon: "folder", active: false, page: "account" },
    {
      title: "",
      icon: "settings",
      active: false,
      page: "settings",
    },
    { title: "", icon: "question", active: false, page: "faq" },
    {
      title: "",
      icon: "question",
      active: false,
      page: "support",
    },
  ]);

  const phones = computed(() => store.state.customer.phonesList);

  const isSelectPoint = (item: string) => {
    return item === String(currentUserPhone.value);
  };
  const updateMenuTitles = () => {
    menuList.value[0].title = t("aside.menu.main");
    menuList.value[1].title = t("aside.menu.settings");
    menuList.value[2].title = t("aside.menu.faq");
    menuList.value[3].title = t("aside.menu.support");
  };

  const getUserPhoneList = () => {
    store.dispatch("customer/getUserPhoneList");
  };

  const changeMainPhone = (phoneNumber: string) => {
    isLoadingSelectPhone.value = true;
    store
      .dispatch("customer/changeMainPhone", phoneNumber)
      .then((isSuccess: boolean) => {
        if (isSuccess) isShowList.value = false;
      })
      .finally(() => {
        isLoadingSelectPhone.value = false;
      });
  };
  const changeMenu = (index: number) => {
    menuList.value.forEach((item) => {
      item.active = false;
    });
    menuList.value[index].active = true;
    router.push(`/${menuList.value[index].page}`);
  };

  const setCurrentPhone = (phoneNumber: string) => {
    changeMainPhone(phoneNumber);
  };

  const clearData = async () => {
    isShowList.value = false;
    store.dispatch("auth/logout");
  };

  const updateActiveMenu = () => {
    const currentRouteName = route.name || "";
    menuList.value.forEach((item) => {
      item.active = item.page === currentRouteName;
    });
  };

  watch(
    () => locale.value,
    () => {
      updateMenuTitles();
    },
    { immediate: true }
  );

  watch(() => route.name, updateActiveMenu);
  watch(
    () => isAuth.value,
    (newIsAuth) => {
      if (newIsAuth) {
        getUserPhoneList();
      }
    }
  );

  onMounted(async () => {
    updateActiveMenu();
    const hasToken = !!getTokenFromCookies();
    if (isAuth.value !== hasToken) {
      store.dispatch("auth/setAuth", hasToken);
    }
  });

  return {
    changeMenu,
    clearData,
    currentUserPhone,
    formatPhoneNumber,
    isAsideHidden,
    isAuth,
    isLoadingSelectPhone,
    isLogoHidden,
    isShowList,
    menuList,
    phones,
    configDataBrand,
    setCurrentPhone,
    t,
    isSelectPoint,
  };
}
